import type { CardBrand } from '@zeni/shared'
import { CARD_BRAND_LABELS } from '../lib/card-brand'

/** 文字マークで描くブランドの券面表記と、その viewBox 幅（高さ 30 に対する比率） */
const WORDMARKS: Partial<Record<CardBrand, { text: string; width: number; italic?: boolean }>> = {
  visa: { text: 'VISA', width: 60, italic: true },
  diners: { text: 'DINERS', width: 84 },
  unionpay: { text: 'UnionPay', width: 96 },
  discover: { text: 'DISCOVER', width: 108 },
}

/**
 * 券面に出すカードブランドのマーク。
 *
 * ロゴ画像は持たず SVG で描く。ブランドカラーが識別に効くもの（Mastercard・JCB・
 * AMEX）だけ色付きの図形にし、残りは文字マークにして親の文字色（currentColor）へ
 * 馴染ませる。こうしておくとカードの地色が明るくても暗くても読める。
 *
 * どのブランドも viewBox の高さを 30 に揃えてあるので、呼び出し側は高さのクラス
 * （h-5 など）を渡すだけで大きさが揃う。
 */
export function CardBrandMark({ brand, className = 'h-5' }: {
  brand: CardBrand
  /** 高さを指定するクラス。幅はブランドごとの比率で決まる */
  className?: string
}) {
  const label = CARD_BRAND_LABELS[brand]
  const svgProps = {
    className,
    role: 'img' as const,
    'aria-label': label,
    // 高さのクラスだけで大きさが決まるよう、幅は比率任せにする
    style: { width: 'auto' as const },
  }

  if (brand === 'mastercard') {
    // 重なった 2 つの円。重なりが橙に見えるよう右の円を半透明で乗せる
    return (
      <svg viewBox="0 0 46 30" {...svgProps}>
        <circle cx="17" cy="15" r="14" fill="#EB001B" />
        <circle cx="29" cy="15" r="14" fill="#F79E1B" fillOpacity="0.85" />
      </svg>
    )
  }

  if (brand === 'jcb') {
    return (
      <svg viewBox="0 0 60 30" {...svgProps}>
        {([['#0E4C96', 'J', 0], ['#BE0029', 'C', 20], ['#007B40', 'B', 40]] as const).map(([fill, letter, x]) => (
          <g key={letter}>
            <rect x={x + 1} y="1" width="18" height="28" rx="3" fill={fill} />
            <text
              x={x + 10}
              y="16"
              textAnchor="middle"
              dominantBaseline="central"
              fill="#ffffff"
              fontSize="15"
              fontWeight="700"
              fontFamily="Helvetica, Arial, sans-serif"
            >
              {letter}
            </text>
          </g>
        ))}
      </svg>
    )
  }

  if (brand === 'amex') {
    return (
      <svg viewBox="0 0 56 30" {...svgProps}>
        <rect x="0" y="0" width="56" height="30" rx="4" fill="#2E77BC" />
        <text
          x="28"
          y="16"
          textAnchor="middle"
          dominantBaseline="central"
          fill="#ffffff"
          fontSize="14"
          fontWeight="700"
          fontFamily="Helvetica, Arial, sans-serif"
        >
          AMEX
        </text>
      </svg>
    )
  }

  const wordmark = WORDMARKS[brand] ?? { text: label, width: label.length * 13 }
  return (
    <svg viewBox={`0 0 ${wordmark.width} 30`} {...svgProps}>
      <text
        x={wordmark.width / 2}
        y="16"
        textAnchor="middle"
        dominantBaseline="central"
        fill="currentColor"
        fontSize="22"
        fontWeight="700"
        fontStyle={wordmark.italic ? 'italic' : undefined}
        fontFamily="Helvetica, Arial, sans-serif"
        // フォントに依存せず幅を viewBox に合わせる（大きさが揃わないと券面で浮く）
        textLength={wordmark.width - 4}
        lengthAdjust="spacingAndGlyphs"
      >
        {wordmark.text}
      </text>
    </svg>
  )
}
